import { Routes, Route } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { useEffect, Suspense } from "react";
import { useAppDispatch } from "@/store";
import { clearAuth } from "@/store/slices/authSlice";
import { preloadGoogleMaps } from "@/utils/googleMapsOptimizer";
import { useSearchHistorySync } from "@/hooks/useSearchHistorySync";
import { GoogleMapsProvider } from "@/components/property/PropertyMap/GoogleMapsLoader";
import { SocketProvider } from "@/contexts/SocketContext";

// Layout Components
import MainLayout from "@/components/layout/MainLayout/MainLayout";
import DashboardLayout from "@/components/layout/DashboardLayout/DashboardLayout";
import AuthLayout from "@/components/layout/AuthLayout/AuthLayout";

// Direct imports for main public pages (no lazy loading)
import Home from "@/pages/public/Home/Home";
import Properties from "@/pages/public/Properties/Properties";
import Agents from "@/pages/public/Agents/Agents";
import About from "@/pages/public/About/About";
import Contact from "@/pages/public/Contact/Contact";

// Keep lazy loading for less critical pages
import {
  LazyLogin,
  LazyRegister,
  LazyForgotPassword,
  LazyResetPassword,
  LazyActivateAccount,
  LazyOTPVerification,
  LazyOnboarding,
  LazyPropertyDetailsPage,
  LazyPropertySearch,
  LazyAgentProfilePage,
  LazyDashboardRouter,
} from "@/components/LazyComponents";

// Dashboard Pages - Customer
import {
  LazyDashboard,
  LazyCustomerProfile,
  LazySavedProperties,
  LazyCustomerBookings,
  LazyDocuments,
  LazyDocumentDetails,
  LazyHelp,
  LazyMyAgent,
  LazyCustomerMessages,
  LazyMessageHistory,
  LazyMessageSettings,
} from "@/components/LazyComponents";

// Dashboard Pages - Agent & Admin (import from lazy components)
import {
  LazyAgentOverview,
  LazyAgentLeads,
  LazyClientPanel,
  LazyAgentBookings,
  LazyAgentAvailability,
  LazyAgentContacts,
  LazyAgentTasks,
  LazyAgentMessages,
  LazyAgentCommunication,
  LazyAdminUsers,
  LazyAdminProperties,
  LazyAdminTours,
  LazyAdminTeamChat,
  LazyAdminAlerts,
  LazyAdminSettings,
  LazyAdminAnalytics,
  LazyProtectedRoute,
} from "@/components/LazyComponents";

// Shared Components
import LoadingSpinner from "@/components/common/LoadingSpinner/LoadingSpinner";

function App() {
  const dispatch = useAppDispatch();

  // Validate auth state on app load (only once)
  useEffect(() => {
    const token = localStorage.getItem("token");
    const userStr = localStorage.getItem("user");

    // Only clear if we have a token but no user data in localStorage
    // Don't check Redux state immediately as it may not be hydrated yet
    if (token && !userStr) {
      console.warn(
        "⚠️ Invalid auth state: token exists but no user data. Clearing auth."
      );
      localStorage.removeItem("token");
      localStorage.removeItem("userType");
      dispatch(clearAuth());
    } else if (userStr && !token) {
      console.warn(
        "⚠️ Invalid auth state: user data exists but no token. Clearing auth."
      );
      localStorage.removeItem("user");
      localStorage.removeItem("userType");
      dispatch(clearAuth());
    }
  }, [dispatch]); // Only run once on mount

  // Preload Google Maps on app initialization (DNS prefetch)
  useEffect(() => {
    preloadGoogleMaps();
  }, []);

  // Enable search history sync for authenticated users
  useSearchHistorySync();

  return (
    <SocketProvider>
      <GoogleMapsProvider>
        <div className="App">
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<MainLayout />}>
              <Route index element={<Home />} />
              <Route path="properties" element={<Properties />} />
              <Route path="agents" element={<Agents />} />
              <Route path="about" element={<About />} />
              <Route path="contact" element={<Contact />} />
              <Route
                path="property/:id"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyPropertyDetailsPage />
                  </Suspense>
                }
              />
              <Route
                path="agent/:id"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyAgentProfilePage />
                  </Suspense>
                }
              />
              <Route
                path="search"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyPropertySearch />
                  </Suspense>
                }
              />
            </Route>

            {/* Auth Routes */}
            <Route path="/auth" element={<AuthLayout />}>
              <Route
                path="login"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyLogin />
                  </Suspense>
                }
              />
              <Route
                path="register"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyRegister />
                  </Suspense>
                }
              />
              <Route
                path="otp-verification"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyOTPVerification />
                  </Suspense>
                }
              />
              <Route
                path="register/onboarding"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyOnboarding />
                  </Suspense>
                }
              />
              <Route
                path="forgot-password"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyForgotPassword />
                  </Suspense>
                }
              />
              <Route
                path="reset-password"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyResetPassword />
                  </Suspense>
                }
              />
            </Route>

            {/* Activation Route (no auth layout needed) */}
            <Route
              path="/activate-account"
              element={
                <Suspense fallback={<LoadingSpinner />}>
                  <LazyActivateAccount />
                </Suspense>
              }
            />

            {/* Main Dashboard Route - redirects based on user type */}
            <Route
              path="/dashboard"
              element={
                <LazyProtectedRoute>
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyDashboardRouter />
                  </Suspense>
                </LazyProtectedRoute>
              }
            />

            {/* Customer Dashboard Routes */}
            <Route
              path="/dashboard/customer"
              element={
                <LazyProtectedRoute allowedTypes={["customer"]}>
                  <DashboardLayout />
                </LazyProtectedRoute>
              }
            >
              <Route
                index
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyDashboard />
                  </Suspense>
                }
              />
              <Route
                path="profile"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyCustomerProfile />
                  </Suspense>
                }
              />
              <Route
                path="saved-properties"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazySavedProperties />
                  </Suspense>
                }
              />
              <Route
                path="bookings"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyCustomerBookings />
                  </Suspense>
                }
              />
              <Route
                path="my-agent"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyMyAgent />
                  </Suspense>
                }
              />
              <Route
                path="documents"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyDocuments />
                  </Suspense>
                }
              />
              <Route
                path="documents/:id"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyDocumentDetails />
                  </Suspense>
                }
              />
              <Route
                path="help"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyHelp />
                  </Suspense>
                }
              />
              <Route
                path="messages"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyCustomerMessages />
                  </Suspense>
                }
              />
              <Route
                path="messages/history"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyMessageHistory />
                  </Suspense>
                }
              />
              <Route
                path="messages/settings"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyMessageSettings />
                  </Suspense>
                }
              />
            </Route>

            {/* Agent Dashboard Routes */}
            <Route
              path="/dashboard/agent"
              element={
                <LazyProtectedRoute allowedTypes={["agent"]}>
                  <DashboardLayout />
                </LazyProtectedRoute>
              }
            >
              <Route
                index
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyAgentOverview />
                  </Suspense>
                }
              />
              <Route
                path="overview"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyAgentOverview />
                  </Suspense>
                }
              />
              <Route
                path="leads"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyAgentLeads />
                  </Suspense>
                }
              />
              <Route
                path="client-panel/:customerId?"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyClientPanel />
                  </Suspense>
                }
              />
              <Route
                path="bookings"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyAgentBookings />
                  </Suspense>
                }
              />
              <Route
                path="availability"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyAgentAvailability />
                  </Suspense>
                }
              />
              <Route
                path="contacts"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyAgentContacts />
                  </Suspense>
                }
              />
              <Route
                path="tasks"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyAgentTasks />
                  </Suspense>
                }
              />
              <Route
                path="messages"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyAgentMessages />
                  </Suspense>
                }
              />
              <Route
                path="communication"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyAgentCommunication />
                  </Suspense>
                }
              />
            </Route>

            {/* Admin Dashboard Routes */}
            <Route
              path="/dashboard/admin"
              element={
                <LazyProtectedRoute allowedTypes={["admin"]}>
                  <DashboardLayout />
                </LazyProtectedRoute>
              }
            >
              <Route
                index
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyAdminUsers />
                  </Suspense>
                }
              />
              <Route
                path="users"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyAdminUsers />
                  </Suspense>
                }
              />
              <Route
                path="properties"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyAdminProperties />
                  </Suspense>
                }
              />
              <Route
                path="tours"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyAdminTours />
                  </Suspense>
                }
              />
              <Route
                path="team-chat"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyAdminTeamChat />
                  </Suspense>
                }
              />
              <Route
                path="alerts"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyAdminAlerts />
                  </Suspense>
                }
              />
              <Route
                path="settings"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyAdminSettings />
                  </Suspense>
                }
              />
              <Route
                path="analytics"
                element={
                  <Suspense fallback={<LoadingSpinner />}>
                    <LazyAdminAnalytics />
                  </Suspense>
                }
              />
            </Route>

            {/* Catch all route */}
            <Route path="*" element={<div>404 - Page Not Found</div>} />
          </Routes>

          {/* Toast Notifications */}
          <Toaster
            position="top-right"
            toastOptions={{
              duration: 4000,
              style: {
                background: "#363636",
                color: "#fff",
              },
              success: {
                duration: 3000,
                iconTheme: {
                  primary: "#4ade80",
                  secondary: "#fff",
                },
              },
              error: {
                duration: 5000,
                iconTheme: {
                  primary: "#ef4444",
                  secondary: "#fff",
                },
              },
            }}
          />
        </div>
      </GoogleMapsProvider>
    </SocketProvider>
  );
}

export default App;
